/**
 * /authors/submit — public author submission form for the marketplace.
 *
 * Submissions land in Mongo `book_submissions`. Founder reviews them in
 * /admin, and approved books get promoted into the `books` collection.
 * Payouts (70/30) will run through Stripe Connect once configured — the
 * copy on this page makes that expectation explicit.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { submitAuthorBook } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { errorMessage } from "@/lib/errorMessage";
import useSEO from "@/lib/useSEO";

const CATEGORIES = ["wealth", "retirement", "saving", "budget", "youth"];

const initial = {
  author_name: "",
  author_email: "",
  title: "",
  blurb: "",
  category: "retirement",
  price_usd: 9,
  sample_url: "",
  cover_url: "",
  why_audience: "",
};

export default function AuthorsSubmit() {
  useSEO({
    title: "Submit your book · Centennial author marketplace",
    description:
      "Independent authors of personal-finance books can submit their ebook to Centennial. Approved authors keep 70% of every sale. We handle checkout, hosting, and traffic.",
    canonicalPath: "/authors/submit",
  });

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e?.target?.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await submitAuthorBook(form);
      setDone(true);
      track(EVENTS.AUTHOR_SUBMISSION, { category: form.category, price_usd: form.price_usd });
      toast.success("Submission received. We reply within 5 business days.");
      return r;
    } catch (err) {
      toast.error(errorMessage(err, "Could not submit. Check the form and try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <div
          data-testid="author-submit-success"
          className="max-w-lg w-full bg-white border border-[#E5E2DC] rounded-2xl p-8 text-center"
        >
          <CheckCircle2 size={40} className="text-[#2B5339] mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-[#1C2B23]">Submission received</h1>
          <p className="text-sm text-[#5C6B62] mt-3 leading-relaxed">
            Thanks for sending us <span className="font-medium text-[#1C2B23]">{form.title}</span>.
            We read every submission ourselves and reply within 5 business days. If we approve
            your book, you&apos;ll get onboarding instructions and a 70/30 revenue share agreement.
          </p>
          <Link
            to="/books"
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#2B5339] hover:underline"
          >
            Back to the shelf →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Link
          to="/books"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> Back to books
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-2">
          <PenLine size={12} /> Author marketplace
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] leading-tight">
          Submit your book.
        </h1>
        <p className="mt-4 text-lg text-[#5C6B62] leading-relaxed">
          Independent authors of personal-finance books — this shelf is open to you. Approved
          authors keep 70% of every sale. We handle checkout, hosting, and traffic.
        </p>

        <div className="mt-6 bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-4 text-xs text-[#1C2B23] flex items-start gap-2">
          <Sparkles size={13} className="text-[#2B5339] mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">How it works:</span> submit → we review within 5 business
            days → approved authors sign a short revenue-share agreement → your book goes live on
            /books. Payouts land monthly via Stripe (Connect setup happens after approval).
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="author-submit-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" required>
              <input
                data-testid="author-name"
                required
                value={form.author_name}
                onChange={set("author_name")}
                className={inputCls}
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="author-email"
                required
                type="email"
                value={form.author_email}
                onChange={set("author_email")}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Book title" required>
            <input
              data-testid="book-title"
              required
              maxLength={140}
              value={form.title}
              onChange={set("title")}
              className={inputCls}
            />
          </Field>

          <Field label="Short blurb (20–600 chars)" required>
            <textarea
              data-testid="book-blurb"
              required
              minLength={20}
              maxLength={600}
              rows={3}
              value={form.blurb}
              onChange={set("blurb")}
              className={inputCls}
            />
            <Counter len={form.blurb.length} max={600} />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category" required>
              <select
                data-testid="book-category"
                required
                value={form.category}
                onChange={set("category")}
                className={inputCls}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c[0].toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Suggested price (USD)" required>
              <input
                data-testid="book-price"
                required
                type="number"
                min={0}
                max={200}
                step={0.01}
                value={form.price_usd}
                onChange={set("price_usd")}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Sample chapter URL (optional)">
            <input
              data-testid="book-sample-url"
              type="url"
              value={form.sample_url}
              onChange={set("sample_url")}
              placeholder="https://drive.google.com/…"
              className={inputCls}
            />
          </Field>

          <Field label="Cover image URL (optional)">
            <input
              data-testid="book-cover-url"
              type="url"
              value={form.cover_url}
              onChange={set("cover_url")}
              placeholder="https://…"
              className={inputCls}
            />
          </Field>

          <Field label="Why should our audience read it? (20–600 chars)" required>
            <textarea
              data-testid="book-why"
              required
              minLength={20}
              maxLength={600}
              rows={4}
              value={form.why_audience}
              onChange={set("why_audience")}
              className={inputCls}
            />
            <Counter len={form.why_audience.length} max={600} />
          </Field>

          <button
            data-testid="author-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1C2B23] text-white rounded-lg py-3 text-sm font-medium hover:bg-[#2B5339] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Submit for review
          </button>

          <p className="text-[11px] text-[#5C6B62] leading-relaxed">
            By submitting, you confirm you hold rights to the work and agree to a 70/30 revenue
            share (70% author / 30% Centennial) on approval. No fees to submit.
          </p>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5C6B62] font-medium">
        {label} {required && <span className="text-[#C26747]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Counter({ len, max }) {
  return (
    <div className="text-[10px] text-[#5C6B62] mt-1 text-right">
      {len}/{max}
    </div>
  );
}
